import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from "react-router";
import backIcon from "../../../../assests/img/back-icon.svg"
import {
  CATEGORY_LIST,
  PRODUCT_DELTA_CATEGORY_LIST,
  DELETE_ALL_ENVIRONMENTS_LABEL,
  DELETED_FROM_ALL_ENVIRONMENTS,
  DELETED_FROM_CURRENT_ENVIRONMENT,
  FULL_DELETE_PERMISSION,
} from '../ProductConstants';
import { AppContext } from 'Context/AppContext';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { ProductsContext } from '../ProductsContext';
import Breadcrumbs from 'Components/DesignSystem/Breadcrumbs/Breadcrumbs';
import Button from 'Components/DesignSystem/Button/Button';
import Divider from 'Components/DesignSystem/Divider/Divider';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { useDismissable } from 'Components/DesignSystem/useDismissable';
import { RowAction } from 'Components/DesignSystem/Iconography/Icons';
import { toast } from 'Components/DesignSystem/Toast/useToast';

interface ProductViewHeaderProps {
  productName: string;
  productid: string
  categoryid: string
}

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

/**
 * The product detail header, on the design system since § 76: DS `Breadcrumbs` (the
 * legacy trail hand-rolled four crumbs, two of them fake `to="#"` links), DS `Button`s,
 * the overflow menu on DS `Menu` + caller-level `useDismissable` (§ 65 pattern — the
 * manual outside-click/scroll listeners and fixed-position math are gone), the delete
 * confirm on DS `Dialog` (was BootBox), and every toast on the DS store. Since § 209
 * the confirm commits to the in-flight delete: Delete wears the DS loading state and
 * Cancel/X/Escape are shut until the request lands (see `deleting` below).
 */
const ProductViewHeader: FC<ProductViewHeaderProps> = ({
  productName,
  productid,
  categoryid
}) => {
  const AppData = useContext(AppContext)
  const channelKey = AppData?.channelKey;
  const industryId = AppData?.get_selected_industry()?.id;
  const prodContext = useContext(ProductsContext);
  const navigate = useNavigate();
  const updatePermission = AppData.pm.checkPermission("gmsproducts_update");
  const deletePermission = AppData.pm.checkPermission("gmsproducts_delete");
  // Only gmsproducts_fulldelete holders see "Also delete … from all other environments";
  // for everyone else it is hidden and the delete is current-environment only.
  const fullDeletePermission = AppData.pm.checkPermission(FULL_DELETE_PERMISSION);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  /*
      § 209 — the one true Tier-3 confirm: nothing on this page carried an
      in-flight signal, so the dialog used to close INSTANTLY while the delete
      ran with no feedback until the toast — and the overflow menu could be
      reopened to fire a second delete mid-flight. While `deleting` the dialog
      is COMMITTED: the call is not abortable, and a mid-flight close would
      only hide the dialog while the toasts + `navigate(-1)` still land.
      `handle_delete_Product` is untouched; the flag wraps it (set before,
      reset in `finally` — `doGet` reports failures via `errorfound` rather
      than throwing, so the guard cannot wedge shut).
  */
  const [deleting, setDeleting] = useState<boolean>(false);
  /* "Also delete this product from all other environments" — unchecked by default
     and reset every time the dialog opens, so a previous choice never carries into
     the next delete. Checked, the backend removes the product from every
     environment under the current channel (`?all_environments=true`). */
  const [allEnvironments, setAllEnvironments] = useState<boolean>(false);
  const menuWrapperRef = useRef<HTMLDivElement>(null);

  // The category-type breadcrumb below reads prodContext.categoryData, which used to be
  // populated by a useEffect inside ProductsContext itself (mounted globally, fired on every
  // industry switch app-wide). This page has no trigger of its own -- it only worked because
  // that global effect had already run by the time this page mounted. Deep-linking straight to
  // this route in a tab (no prior visit to /gms/products) would otherwise show an empty
  // breadcrumb, since nothing else fetches this cache for this route.
  useEffect(() => {
    if (industryId) {
      prodContext.getCategoryTypeAndCategory();
    }
  }, [industryId]);

  /* Caller-level dismiss spanning trigger + menu (§ 65): Escape and outside
     mousedown close it; the wrapper contains both so a trigger click toggles
     instead of fighting the dismiss. The legacy close-on-scroll listener is gone
     with the fixed positioning that needed it — the menu scrolls with the page. */
  useDismissable({
    isOpen: isMenuOpen,
    onDismiss: () => setIsMenuOpen(false),
    isInside: (target) => !!menuWrapperRef.current?.contains(target),
  });

  const handle_delete_Product = async (deleteEverywhere: boolean) => {
    if (!deletePermission) {
      toast.info("You don't have permission to delete the product.", {
        description: "Reason: Insufficient privileges",
      });
      return;
    }
    let res = await ServiceWrapper.doGet(
      api_base_url + "product/delete/channel/" + channelKey + "/product/" + productid
        + "?all_environments=" + (deleteEverywhere ? "true" : "false"),
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      AppData.setProductDeleted(res.data.done);
      if (res.data.done) {
        AppData.reset();

        // The toast reports what the user ASKED for. Checked removes the product from
        // every environment of this domain in the current workspace; unchecked from this
        // one. Either can leave it elsewhere (another environment, or another workspace
        // sharing the domain), and that is what the follow-up info line is for.
        toast.success(deleteEverywhere ? DELETED_FROM_ALL_ENVIRONMENTS : DELETED_FROM_CURRENT_ENVIRONMENT);
        if (res.data.remaining_channels && res.data.remaining_channels.length > 0) {
          toast.info(`Product still available in: ${res.data.remaining_channels.join(', ')}`);
        }
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        // Failure cases with specific error handling.
        const failedName = res.data.name || "Product";
        // `?? ""` — a done:false response with NO message field used to throw here
        // (`undefined.includes`), which surfaced as an unhandled rejection and no
        // feedback at all. Guarded in § 76 so it lands in the generic branch below.
        const message: string = res.data.message ?? "";

        if (message.includes("does not exist")) {
          toast.error("Unable to delete product.", {
            description: `Reason: ${failedName} does not exist`,
          });
        } else if (message.includes("not mapped to")) {
          toast.warning("Unable to delete product.", {
            description: message.includes("your Workspace")
              ? "Reason: Product is not mapped to any environment of this domain in your workspace"
              : "Reason: Product is not mapped to this environment",
          });
        } else if (
          message.includes("technical error") ||
          message.includes("Neo4j Error") ||
          message.includes("Graph DB Error")
        ) {
          toast.error("Unable to delete product.", {
            description: "Reason: Technical error occurred during deletion",
          });
        } else {
          toast.error("Unable to delete product.", {
            description: `Reason: ${message || "Failed to delete product"}`,
          });
        }
      }
    } else {
      toast.error("Unable to delete product.", {
        description: "Reason: Network error occurred while deleting product",
      });
    }
  };

  const onDeleteMenuItem = () => {
    setIsMenuOpen(false);
    if (deletePermission) {
      setAllEnvironments(false);
      setConfirmOpen(true);
    } else {
      toast.info("You don't have permission to delete this product");
    }
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await handle_delete_Product(fullDeletePermission && allEnvironments);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const categoryName = prodContext.categoryData?.map((categoryType) => categoryType.category).flat().find(category => category.id === categoryid)?.name

  return (
    <React.Fragment>
      <Breadcrumbs
        id="product-detail-breadcrumb"
        data-testid="product-detail-breadcrumb"
        items={[
          {
            label: CATEGORY_LIST,
            href: PRODUCT_DELTA_CATEGORY_LIST,
            onClick: (e) => { e.preventDefault(); navigate(PRODUCT_DELTA_CATEGORY_LIST); },
            'data-testid': 'product-detail-breadcrumb-link-categories',
          },
          {
            /* Waypoint (§ 69): a grouping level, not a destination — the legacy crumb
               was a fake `to="#"` link. Neither href nor onClick → a plain span. */
            label: prodContext.findSelectedCategoryTypeName(prodContext.categoryData, categoryid) ?? "",
            'data-testid': 'product-detail-breadcrumb-link-category-type',
          },
          {
            label: categoryName ?? "",
            href: `/gms/products/list/${categoryid}`,
            onClick: (e) => { e.preventDefault(); navigate(`/gms/products/list/${categoryid}`); },
            'data-testid': 'product-detail-breadcrumb-link-category',
          },
          {
            /* Last item → aria-current="page"; the legacy trail linked it to "#". */
            label: productName,
            'data-testid': 'product-detail-breadcrumb-link-product',
          },
        ]}
      />
      <div className={`tw-flex tw-justify-between tw-items-center`}>
        <div className={`tw-mt-3 tw-flex tw-items-center tw-gap-2.5`}>
          <Link id="product-detail-btn-back" data-testid="product-detail-btn-back" to={`/gms/products/list/${categoryid}`} aria-label="Back to product list">
            <img className={`tw-cursor-pointer`} src={backIcon} alt="" />
          </Link>
          <Typography className="tw-text-ink" variant="display" as="h1" id="product-detail-title">
            {productName}
          </Typography>
        </div>
        <div className={`tw-flex tw-gap-2 tw-items-center`}>
          <Button
            variant="primary"
            id="product-detail-btn-edit"
            data-testid="product-edit-btn"
            onClick={() => { if (updatePermission) { navigate(`/gms/product/edit/${productid}`) } else { toast.info("You don't have permission to Update the Products."); } }}
          >
            Edit Product
          </Button>
          <div className="tw-relative" ref={menuWrapperRef}>
            <Button
              variant="subtle"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              id="product-detail-btn-overflow"
              data-testid="productview-productviewheader-button-1"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <RowAction />
            </Button>

            {isMenuOpen && (
              <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-40">
                <Menu
                  variant="compact"
                  data-testid="product-detail-overflow-menu"
                  items={[
                    {
                      id: "delete",
                      label: "Delete",
                      'data-testid': "productview-productviewheader-button-2",
                      onClick: onDeleteMenuItem,
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Divider className="tw-mt-2" data-testid="product-detail-divider" />

      <Dialog
        isOpen={confirmOpen}
        onClose={closeDeleteConfirm}
        title="Do you really want to delete?"
        tone="danger"
        data-testid="product-delete-confirm-dialog"
        footer={
          /* The Dialog footer slot is a plain block — the caller owns the row.
             This was a bare fragment, so the two buttons STACKED left-aligned;
             the house wrapper (same as every other confirm) puts them in one
             right-aligned row. */
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
            <Button variant="secondary" data-testid="product-delete-cancel-btn" disabled={deleting} onClick={closeDeleteConfirm}>
              Cancel
            </Button>
            <Button
              variant="danger-primary"
              data-testid="product-delete-confirm-btn"
              loading={deleting}
              onClick={confirmDelete}
            >
              Confirm
            </Button>
          </div>
        }
      >
        {/* The ticket's simple format: the question is the title, the product name is the
            body, then the opt-in — no warning box and no "Would you like to proceed?" line,
            unlike the bulk and Upload XLS dialogs. */}
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Typography variant="title-header" as="p" className="tw-text-ink" data-testid="product-delete-confirm-name">
            {productName}
          </Typography>
          {fullDeletePermission && (
            <Checkbox
              label={DELETE_ALL_ENVIRONMENTS_LABEL}
              checked={allEnvironments}
              onChange={(checked) => setAllEnvironments(checked)}
              disabled={deleting}
              data-testid="product-delete-all-environments-checkbox"
            />
          )}
        </div>
      </Dialog>
    </React.Fragment>
  )
}

export default ProductViewHeader;
